import { CurrencyPipe, DatePipe } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	type OnInit,
	signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import {
	TableModule,
	type TablePageEvent,
	type TableSelectAllChangeEvent
} from 'primeng/table';
import { ToastService } from '../../services/toast.service';
import { ReportHttpService } from '../../services/report-http.service';
import { ReportExportService, type ReportRow } from './report-export.service';
import { type InvoiceAgeReport } from '../../models/invoice-age-report';
import { ParentCompanyStore } from '../../stores/parent-company-store';

interface KpiStat {
	label: string;
	value: string;
}

interface ReportOption {
	label: string;
	value: string;
}

const REPORT_VIEW_OPTION: ReportOption = { label: 'Report', value: 'report' };
const DISPUTED_REPORT_VIEW_OPTION: ReportOption = {
	label: 'Disputed Report',
	value: 'disputed'
};
const REPORT_VIEW_OPTIONS: ReportOption[] = [
	REPORT_VIEW_OPTION,
	DISPUTED_REPORT_VIEW_OPTION
];

@Component({
	selector: 'app-reporting',
	standalone: true,
	imports: [
		FormsModule,
		SelectModule,
		DatePickerModule,
		ButtonModule,
		InputTextModule,
		TooltipModule,
		TableModule
	],
	templateUrl: './reporting.component.html',
	styleUrl: './reporting.component.sass',
	providers: [CurrencyPipe, DatePipe, ReportExportService]
})
export class ReportingComponent implements OnInit {
	private static readonly DUE_DATE_TERM_DAYS = 30;

	// Columns shown in the on-screen table: everything through OT Status, plus
	// Requisitioner and OI Status. The remaining REPORT_COLUMNS stay export-only.
	private static readonly TABLE_COLUMN_FIELDS = new Set([
		'customerJobLocation',
		'transactionType',
		'date',
		'documentNumber',
		'poNumber',
		'jobLocation',
		'rkSubmitter',
		'dueDate',
		'age',
		'openBalance',
		'memo',
		'soNumbers',
		'otStatus',
		'requisitioner',
		'oiStatus'
	]);

	// Shown only in the Disputed Report view, in the same order as the Excel export.
	private static readonly DISPUTED_TABLE_COLUMN_FIELDS = new Set([
		'otSubDate',
		'otDisputedDate',
		'otDisputedResubmission',
		'otApprovedDate',
		'otDisputeNote',
		'detailedNotes'
	]);

	// These share a fixed width so long note text truncates with an ellipsis
	// instead of stretching the table.
	private static readonly FIXED_WIDTH_COLUMN_FIELDS = new Set([
		'jobLocation',
		'otDisputeNote',
		'detailedNotes'
	]);

	private readonly toastService = inject(ToastService);
	private readonly reportHttpService = inject(ReportHttpService);
	private readonly reportExportService = inject(ReportExportService);
	private readonly currencyPipe = inject(CurrencyPipe);
	private readonly datePipe = inject(DatePipe);
	private readonly parentCompanyStore = inject(ParentCompanyStore);

	protected currentMonthLabel =
		this.datePipe.transform(new Date(), 'MMMM') ?? '';
	protected monthlyStats: KpiStat[] = [
		{ label: 'Invoice Total Billed', value: '$0' },
		{ label: 'Invoices Submitted', value: '0' }
	];

	protected outstandingStats: KpiStat[] = [
		{ label: 'Disputed', value: '0' },
		{ label: 'Saved', value: '0' },
		{ label: 'Submitted', value: '0' },
		{ label: 'Approved', value: '0' }
	];

	protected readonly clientOptions = computed<ReportOption[]>(() => [
		{ label: 'All Clients', value: 'all' },
		...this.parentCompanyStore
			.parentCompanies()
			.map((company) => ({ label: company.label, value: company.name }))
	]);

	protected readonly reportViewOptions = REPORT_VIEW_OPTIONS;
	protected readonly reportView = signal<ReportOption>(REPORT_VIEW_OPTION);
	protected readonly selectedClient = signal<ReportOption | null>(null);
	protected readonly startDate = signal<Date | null>(null);
	protected readonly endDate = signal<Date | null>(null);
	protected readonly searchTerm = signal('');
	protected readonly tableFirst = signal(0);
	protected readonly reportColumns = computed(() => {
		const fields =
			this.reportView().value === 'disputed'
				? new Set([
						...ReportingComponent.TABLE_COLUMN_FIELDS,
						...ReportingComponent.DISPUTED_TABLE_COLUMN_FIELDS
					])
				: ReportingComponent.TABLE_COLUMN_FIELDS;

		return ReportExportService.REPORT_COLUMNS.filter((col) =>
			fields.has(col.field)
		);
	});
	protected readonly reportData = signal<ReportRow[]>([]);
	protected isReportTableLoading = false;
	protected readonly selectedRows = signal<ReportRow[]>([]);
	protected readonly isInvoiceMetricsLoading = signal(false);
	protected readonly isSoPendingBillingLoading = signal(false);
	protected readonly isDownloadLoading = signal(false);

	// Raw invoices, kept only for building the Excel export (their OT/OI
	// dates are no longer shown as table columns).
	private rawInvoices: InvoiceAgeReport[] = [];

	protected readonly filteredReportData = computed<ReportRow[]>(() => {
		const term = this.searchTerm().trim().toLowerCase();
		const clientValue = (
			this.selectedClient()?.value ?? 'all'
		).toLowerCase();
		const start = this.startDate();
		const end = this.endDate();
		const startDate = start ? ReportingComponent.toMidnight(start) : null;
		const endDate = end ? ReportingComponent.toMidnight(end) : null;
		const disputedOnly = this.reportView().value === 'disputed';

		return this.reportData().filter((row) => {
			const matchesView =
				!disputedOnly || ReportExportService.isDisputed(row);

			const matchesClient =
				clientValue === 'all' ||
				(row.customerJobLocation ?? '')
					.toLowerCase()
					.includes(clientValue);

			const matchesSearch =
				!term ||
				Object.values(row).some((value) =>
					String(value ?? '')
						.toLowerCase()
						.includes(term)
				);

			const matchesDateRange = ReportingComponent.isWithinDateRange(
				row.date,
				startDate,
				endDate
			);

			return (
				matchesView &&
				matchesClient &&
				matchesSearch &&
				matchesDateRange
			);
		});
	});

	// Bound to the table's [selectAll]
	protected readonly allSelected = computed<boolean>(() => {
		const filtered = this.filteredReportData();
		if (!filtered.length) return false;

		const selectedIds = new Set(this.selectedRows().map((row) => row.id));
		return filtered.every((row) => selectedIds.has(row.id));
	});

	public constructor() {
		effect(() => {
			this.reportView();
			this.searchTerm();
			this.selectedClient();
			this.startDate();
			this.endDate();
			this.tableFirst.set(0);
		});

		effect(() => {
			this.reportView();
			this.selectedRows.set([]);
		});
	}

	public ngOnInit(): void {
		this.loadReportData();
		this.parentCompanyStore.load();
	}

	protected onSelectAllChange({ checked }: TableSelectAllChangeEvent): void {
		this.selectedRows.set(checked ? [...this.filteredReportData()] : []);
	}

	protected onPageChange({ first }: TablePageEvent): void {
		this.tableFirst.set(first);
	}

	protected onSelectionChange(rows: ReportRow[]): void {
		this.selectedRows.set(rows);
	}

	protected isFixedWidthColumn(field: string): boolean {
		return ReportingComponent.FIXED_WIDTH_COLUMN_FIELDS.has(field);
	}

	protected async onDownload(): Promise<void> {
		const isDisputedView = this.reportView().value === 'disputed';
		const rows = isDisputedView
			? this.selectedRows().filter((row) =>
					ReportExportService.isDisputed(row)
				)
			: this.selectedRows();

		if (!rows.length) {
			this.toastService.showErrorToast(
				'Select at least one row to export.'
			);
			return;
		}

		this.isDownloadLoading.set(true);
		try {
			await this.reportExportService.downloadWorkbook(
				rows.map((row) => this.buildExportRow(row)),
				isDisputedView ? 'Disputed Report' : undefined,
				isDisputedView
			);
		} finally {
			this.isDownloadLoading.set(false);
		}
	}

	protected onDownloadInvoiceMetrics(): void {
		this.isInvoiceMetricsLoading.set(true);
		this.reportHttpService.getInvoiceSubmissions().subscribe({
			next: (logs) => {
				void this.reportExportService
					.downloadInvoiceMetricsWorkbook(logs)
					.finally(() => {
						this.isInvoiceMetricsLoading.set(false);
					});
			},
			error: () => {
				this.toastService.showErrorToast(
					'Error loading invoice metrics'
				);
				this.isInvoiceMetricsLoading.set(false);
			}
		});
	}

	protected onDownloadSoPendingBilling(): void {
		this.isSoPendingBillingLoading.set(true);
		this.reportHttpService.getSoPendingBillingReport().subscribe({
			next: (rows) => {
				this.reportExportService.downloadSoPendingBillingCsv(rows);
				this.isSoPendingBillingLoading.set(false);
			},
			error: () => {
				this.toastService.showErrorToast(
					'Error loading SO pending billing report'
				);
				this.isSoPendingBillingLoading.set(false);
			}
		});
	}

	protected onResetFilters(): void {
		this.searchTerm.set('');
		this.selectedClient.set(null);
		this.startDate.set(null);
		this.endDate.set(null);
	}

	private loadReportData(): void {
		this.isReportTableLoading = true;
		this.reportHttpService.getInvoiceAgingReport().subscribe({
			next: (invoices: InvoiceAgeReport[]) => {
				this.rawInvoices = invoices;
				this.reportData.set(
					invoices.map((invoice) => this.toReportRow(invoice))
				);
				this.monthlyStats = this.computeCurrentMonthStats(invoices);
				this.outstandingStats = this.computeOutstandingStats(invoices);
				this.isReportTableLoading = false;
			},
			error: () => {
				this.toastService.showErrorToast('Error loading report data');
				this.isReportTableLoading = false;
			}
		});
	}

	// the day-count columns are computed from the real values.
	private buildExportRow(row: ReportRow): ReportRow {
		const invoice = this.rawInvoices.find(
			(candidate) => candidate.internalId === row.id
		);
		if (!invoice) return row;

		const reportRow = this.toReportRow(invoice);

		return { ...reportRow, disputedNote: invoice.oiDisputedNote ?? '' };
	}

	private toReportRow(invoice: InvoiceAgeReport): ReportRow {
		return {
			id: invoice.internalId,
			amount: invoice.amount,
			customerJobLocation: invoice.name,
			transactionType: 'Invoice',
			date: invoice.date ?? '',
			rkSubmitter: invoice.rkSubmitter,
			documentNumber: this.extractDocumentNumber(invoice.invoiceNumber),
			poNumber: invoice.po,
			jobLocation: invoice.jobLocation,
			memo: invoice.lineDescription,
			soNumbers: invoice.ticketNumber,
			otStatus: invoice.otStatus,
			requisitioner: invoice.requisitioner,
			oiStatus: invoice.oiStatus,
			openBalance: this.currencyPipe.transform(invoice.amount) ?? '',
			dueDate: this.calculateDueDate(invoice.date ?? undefined),
			age: this.calculateAge(invoice.date ?? undefined),
			oiSubDate: this.formatOptionalDate(invoice.oiSubDate ?? undefined),
			oiStatusDate: this.formatOptionalDate(
				invoice.oiStatusDate ?? undefined
			),
			paidDate: this.formatOptionalDate(invoice.paidDate ?? undefined),
			otSubDate: this.formatOptionalDate(invoice.otSubDate ?? undefined),
			otDisputedDate: this.formatOptionalDate(
				invoice.otDisputedDate ?? undefined
			),
			otDisputedResubmission: invoice.otDisputedResubmission ?? false,
			otApprovedDate: this.formatOptionalDate(
				invoice.otApprovedDate ?? undefined
			),
			otDisputeNote: invoice.otDisputedNote ?? '',
			detailedNotes: invoice.otDisputedNote ?? '',
			otSubToOtDispDays: ReportingComponent.daysBetween(
				invoice.otDisputedDate ?? undefined,
				invoice.otSubDate ?? undefined
			),
			rkInvToOtSubDays: ReportingComponent.daysBetween(
				invoice.otSubDate ?? undefined,
				invoice.date ?? undefined
			),
			otSubToOtAppDays: ReportingComponent.daysBetween(
				invoice.otApprovedDate ?? undefined,
				invoice.otSubDate ?? undefined
			),
			otAppToOiSubDays: ReportingComponent.daysBetween(
				invoice.oiSubDate ?? undefined,
				invoice.otApprovedDate ?? undefined
			),
			oiSubToOiAppDays: ReportExportService.isStatus(
				invoice.oiStatus,
				'Approved'
			)
				? ReportingComponent.daysBetween(
						invoice.oiStatusDate ?? undefined,
						invoice.oiSubDate ?? undefined
					)
				: '',
			oiSubToPaidDays: ReportingComponent.daysBetween(
				invoice.paidDate ?? undefined,
				invoice.oiSubDate ?? undefined
			)
		};
	}

	private computeCurrentMonthStats(invoices: InvoiceAgeReport[]): KpiStat[] {
		const today = new Date();
		const currentMonthInvoices = invoices.filter((invoice) => {
			if (!invoice.date) return false;
			const invoiceDate = new Date(invoice.date);
			return (
				invoiceDate.getMonth() === today.getMonth() &&
				invoiceDate.getFullYear() === today.getFullYear()
			);
		});

		const totalBilled = currentMonthInvoices.reduce(
			(sum, invoice) => sum + invoice.amount,
			0
		);

		return [
			{
				label: 'Invoice Total Billed',
				value: this.currencyPipe.transform(totalBilled) ?? '$0'
			},
			{
				label: 'Invoices Submitted',
				value: ReportingComponent.countByOtStatus(
					invoices,
					'Submitted'
				).toString()
			}
		];
	}

	private computeOutstandingStats(invoices: InvoiceAgeReport[]): KpiStat[] {
		return [
			{
				label: 'Disputed',
				value: ReportingComponent.countByStatus(
					invoices,
					'Disputed'
				).toString()
			},
			{
				label: 'Saved',
				value: ReportingComponent.countByStatus(
					invoices,
					'Saved'
				).toString()
			},
			{
				label: 'Submitted',
				value: ReportingComponent.countByStatus(
					invoices,
					'Submitted'
				).toString()
			},
			{
				label: 'Approved',
				value: ReportingComponent.countByStatus(
					invoices,
					'Approved'
				).toString()
			}
		];
	}

	private static countByStatus(
		invoices: InvoiceAgeReport[],
		status: string
	): number {
		return invoices.filter(
			(invoice) =>
				ReportExportService.isStatus(invoice.otStatus, status) ||
				ReportExportService.isStatus(invoice.oiStatus, status)
		).length;
	}

	private static countByOtStatus(
		invoices: InvoiceAgeReport[],
		status: string
	): number {
		return invoices.filter((invoice) =>
			ReportExportService.isStatus(invoice.otStatus, status)
		).length;
	}

	private extractDocumentNumber(invoiceNumber: string): string {
		return invoiceNumber.includes('#')
			? (invoiceNumber.split('#').pop() ?? '').trim()
			: invoiceNumber.trim();
	}

	private calculateDueDate(date: string | undefined): string {
		if (!date) return '';

		const invoiceDate = new Date(date);
		invoiceDate.setDate(
			invoiceDate.getDate() + ReportingComponent.DUE_DATE_TERM_DAYS
		);
		return this.datePipe.transform(invoiceDate, 'M/d/yyyy') ?? '';
	}

	private formatOptionalDate(value: string | undefined): string {
		if (!value) return '';
		return this.datePipe.transform(value, 'M/d/yyyy', 'UTC') ?? '';
	}

	private calculateAge(lastModifiedDate: string | undefined): string {
		if (!lastModifiedDate) return '';

		const lastModified = ReportingComponent.toMidnight(
			new Date(lastModifiedDate)
		);
		const today = ReportingComponent.toMidnight(new Date());
		const daysElapsed = Math.round(
			(today.getTime() - lastModified.getTime()) / (1000 * 60 * 60 * 24)
		);

		return daysElapsed.toString();
	}

	private static toMidnight(date: Date): Date {
		return new Date(date.getFullYear(), date.getMonth(), date.getDate());
	}

	private static daysBetween(
		laterValue: string | undefined,
		earlierValue: string | undefined
	): string {
		if (!laterValue || !earlierValue) return '';

		const toUtcMidnight = (value: string): number => {
			const parsed = new Date(value);
			return Date.UTC(
				parsed.getUTCFullYear(),
				parsed.getUTCMonth(),
				parsed.getUTCDate()
			);
		};

		const days = Math.round(
			(toUtcMidnight(laterValue) - toUtcMidnight(earlierValue)) /
				(1000 * 60 * 60 * 24)
		);
		return days.toString();
	}

	private static isWithinDateRange(
		date: string | undefined,
		startDate: Date | null,
		endDate: Date | null
	): boolean {
		if (!startDate && !endDate) return true;
		if (!date) return false;

		const rowDate = ReportingComponent.toMidnight(new Date(date));
		if (startDate && rowDate < startDate) return false;
		if (endDate && rowDate > endDate) return false;

		return true;
	}
}
